import Image from 'next/legacy/image';
import type { FunctionComponent } from 'react';
import { RawAnchor } from '../../components/anchor';
import { Container } from '../../components/layout/grid';
import { Markdown } from '../../components/markdown';
import type { CreditMattersTableFragment } from '../../graphql/fragments/CreditMattersTableFragment.graphql';
import { usePersonalizations } from '../../lib/hooks/usePersonalizations';
import { renderTemplate } from '../../lib/utils/text';
import variables from '../../styles/variables.module.scss';
import { ActionButton } from '../action-button';
import styles from './credit-matters-table.module.scss';

/**
 * Section that displays a credit comparison table.
 */

export const CreditMattersTable: FunctionComponent<Props> = ({
  heading,
  headingColor = 'black',
  backgroundColor = 'white',
  anchorId,
  copy,
  button,
  fineprintMd,
  image,
  rows
}) => {
  const personalizations = usePersonalizations();
  return <div id={anchorId} style={{
    backgroundColor: variables[backgroundColor]
  }} className={styles.wrapper} data-sentry-component="CreditMattersTable" data-sentry-source-file="credit-matters-table.tsx">
            <Container data-sentry-element="Container" data-sentry-source-file="credit-matters-table.tsx">
                <div className={styles.heading}>
                    <h2 style={{
          color: variables[headingColor]
        }}>
                        {heading}
                    </h2>
                </div>
                <div className={styles.copy}>
                    <Markdown typeStyle={{
          _: 'body-md',
          md: 'body-md'
        }} color='black' markdown={renderTemplate(copy, personalizations)} data-sentry-element="Markdown" data-sentry-source-file="credit-matters-table.tsx" />
                </div>
                <div className={styles.buttons}>
                    {!!button && <ActionButton preset={button?.preset} {...button} />}
                </div>
                <table className={styles.table}>
                    <thead>
                        <tr className={styles.tableRow}>
                            <th />
                            <th className={styles.tableHeader}>
                                <div className={styles.imageContainer}>
                                    {image && <Image src={image.url ?? ''} width={image.width} height={image.height} alt={image.alt ?? ''} />}
                                </div>
                            </th>
                            <th className={styles.tableHeader}>
                                <div className={styles.other}>
                                    <div>Very Poor</div>
                                    <div className={styles.otherSubtititle}>
                                        300-600
                                    </div>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows?.items.map((row, i) => <tr key={i}>
                                <td className={styles.tableDataLeft}>
                                    <div>
                                        <div className={styles.bold}>
                                            {row?.rowTitle}
                                        </div>
                                        <span className={styles.learnMore}>
                                            {row?.rowLink && <RawAnchor href={row?.rowLink} preset='plain' className={styles.link} onClick={e => e.stopPropagation()}>
                                                    Learn More
                                                </RawAnchor>}
                                        </span>
                                    </div>
                                </td>
                                <td className={styles.tableData}>
                                    <div>{row?.rowStepValue}</div>
                                </td>
                                <td className={styles.tableData}>
                                    <div>{row?.rowOtherValue}</div>
                                </td>
                            </tr>)}
                    </tbody>
                </table>

                <div className={styles.fineprint}>
                    <Markdown typeStyle={{
          _: 'body-xs'
        }} color='black' markdown={renderTemplate(fineprintMd, personalizations)} data-sentry-element="Markdown" data-sentry-source-file="credit-matters-table.tsx" />
                </div>
            </Container>
        </div>;
};
export default CreditMattersTable;
type Props = Omit<CreditMattersTableFragment, 'sys' | 'type'> & {
  index?: number;
};